import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Drawer, Textarea } from '@mantine/core';
import { IconMessageCircleQuestion, IconRefresh, IconSend } from '@tabler/icons-react';
import {
  PROCEDURES_CHAT_ROLE,
  PROCEDURES_ERROR_CODES,
  type ProcedureChatCitationView,
  type ProcedureChatMessageView,
} from '@actschurch/ccms-sdk';
import { Callout } from '@/components/ui';
import { getChatSessionKey, getDeviceId, resetChatSessionKey } from '@/lib/device';
import { errorMessage, isNetworkError, procedureErrorCode } from '@/lib/errors';
import { useChatApi, type PortalMode } from '@/lib/portalApi';
import { proceduresKeys } from '@/lib/queryKeys';
import { useSyncStore } from '@/lib/syncQueue';

export interface AskSheetProps {
  code: string;
  title: string;
  /** The session's mode (member when signed in). */
  mode: PortalMode;
  /** The run on screen, when it was started in the same mode (so the assistant knows the context). */
  runId?: number | null;
  runToken?: string | null;
  /** The step on screen, so "this step" makes sense. */
  stepId?: number | null;
  stepNumber?: number | null;
  /** Tapping a "Step 4" citation. */
  onGoToStep?: (stepNumber: number) => void;
  /** Lift the button above a sticky action bar: 'bar' (one button) or 'runner' (the runner's taller bar). */
  lift?: 'bar' | 'runner';
}

interface LocalMessage {
  key: string;
  role: 'user' | 'assistant' | 'notice';
  text: string;
  answered?: boolean | null;
  citations?: ProcedureChatCitationView[];
  failed?: boolean;
}

function fromServer(m: ProcedureChatMessageView): LocalMessage {
  const assistant = m.roleID === PROCEDURES_CHAT_ROLE.Assistant || m.role === 'assistant';
  return {
    key: `m${m.messageID}`,
    role: assistant ? 'assistant' : 'user',
    text: m.text,
    answered: m.answered,
    citations: m.citations ?? [],
  };
}

function citationLabel(c: ProcedureChatCitationView): string {
  if (c.kind === 'step' && c.stepNumber) return `Step ${c.stepNumber}`;
  if (c.documentName) return c.documentName;
  return c.kind === 'step' ? 'A step' : 'A document';
}

/**
 * The floating "Ask" button and its bottom-sheet chat with the AI assistant about this procedure.
 * Only rendered when the portal view says aiChatAvailable. Answers cite steps ("Step 4") and
 * documents; a step citation jumps to that step.
 */
export function AskSheet({ code, title, mode, runId, runToken, stepId, stepNumber, onGoToStep, lift }: AskSheetProps) {
  const [open, setOpen] = useState(false);
  const [sessionKey, setSessionKey] = useState(() => getChatSessionKey(code));
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [local, setLocal] = useState<LocalMessage[]>([]);
  const [disabled, setDisabled] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const online = useSyncStore((s) => s.online);
  const listRef = useRef<HTMLDivElement>(null);
  const deviceId = getDeviceId();
  const api = useChatApi(code, mode);

  const history = useQuery({
    queryKey: proceduresKeys.chat(mode, code, sessionKey),
    queryFn: () => api.history(sessionKey, deviceId),
    enabled: open,
    staleTime: Infinity,
    retry: false,
  });

  const server = (history.data ?? []).map(fromServer);
  const messages = [...server, ...local];

  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages.length, sending]);

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || sending || disabled) return;
    if (!online) {
      setLocal((l) => [...l, { key: `n${Date.now()}`, role: 'notice', text: "Ask needs signal. Your ticks still save on this phone. Try again when you're back in range." }]);
      return;
    }
    const userKey = `u${Date.now()}`;
    setLocal((l) => [...l, { key: userKey, role: 'user', text }]);
    setDraft('');
    setSending(true);
    try {
      const reply = await api.ask(
        { sessionKey, message: text, deviceId, runID: runId ?? null, stepID: stepId ?? null },
        runToken,
      );
      setLocal((l) => [
        ...l.filter((m) => m.key !== userKey),
        { ...fromServer(reply.question), key: `q${reply.question.messageID}-${userKey}` },
        { ...fromServer(reply.reply), key: `r${reply.reply.messageID}-${userKey}` },
      ]);
      setRemaining(reply.remainingThisHour ?? null);
    } catch (err) {
      const c = procedureErrorCode(err);
      let notice: string;
      if (c === PROCEDURES_ERROR_CODES.ChatRateLimited) {
        notice = "You've asked a lot of questions in the last hour. Please try again a little later, or ask your team leader.";
      } else if (c === PROCEDURES_ERROR_CODES.ChatDisabled) {
        notice = 'Ask is switched off for this procedure right now.';
        setDisabled(notice);
      } else if (c === PROCEDURES_ERROR_CODES.AiUnavailable) {
        notice = "The assistant isn't available right now. Please try again later.";
      } else if (isNetworkError(err)) {
        notice = "We couldn't reach the assistant. Check your signal and try again.";
      } else {
        notice = errorMessage(err);
      }
      setLocal((l) => [...l.map((m) => (m.key === userKey ? { ...m, failed: true } : m)), { key: `n${Date.now()}`, role: 'notice', text: notice }]);
      setDraft((d) => d || text);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  };

  const newChat = () => {
    setSessionKey(resetChatSessionKey(code));
    setLocal([]);
    setRemaining(null);
  };

  return (
    <>
      <button
        type="button"
        className={`proc-askfab${lift ? ` lift-${lift}` : ''}`}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={`Ask a question about ${title}`}
      >
        <IconMessageCircleQuestion size={24} aria-hidden="true" />
        <span>Ask</span>
      </button>

      <Drawer
        opened={open}
        onClose={() => setOpen(false)}
        position="bottom"
        size="88%"
        title="Ask about this procedure"
        classNames={{ content: 'proc-sheet', body: 'proc-sheet-body', title: 'proc-sheet-title' }}
        transitionProps={{ transition: 'slide-up', duration: 160 }}
      >
        <div className="proc-chat">
          <p className="proc-chat-intro">
            Answers come from this procedure{stepNumber ? ` (you're on step ${stepNumber})` : ''} and its documents.
            For anything about safety, check with your team leader too.
          </p>
          <div className="proc-chat-list" ref={listRef} aria-live="polite" aria-relevant="additions">
            {history.isLoading && <p className="proc-chat-muted">Loading your earlier questions...</p>}
            {messages.length === 0 && !history.isLoading && (
              <p className="proc-chat-muted">Ask anything about this procedure, for example "What do I do if the light stays red?"</p>
            )}
            {messages.map((m) =>
              m.role === 'notice' ? (
                <div key={m.key} className="proc-chat-notice">{m.text}</div>
              ) : (
                <div key={m.key} className={`proc-bubble ${m.role}${m.failed ? ' failed' : ''}`}>
                  <span className="sr-only">{m.role === 'user' ? 'You asked:' : 'Answer:'}</span>
                  <div className="text">{m.text}</div>
                  {m.role === 'assistant' && m.answered === false && (
                    <div className="hint">This isn't covered in the procedure. Please ask your team leader.</div>
                  )}
                  {m.citations && m.citations.length > 0 && (
                    <div className="cites" aria-label="Sources">
                      {m.citations.map((c, i) =>
                        c.kind === 'step' && c.stepNumber && onGoToStep ? (
                          <button
                            key={i}
                            type="button"
                            className="cite"
                            onClick={() => {
                              setOpen(false);
                              onGoToStep(c.stepNumber as number);
                            }}
                          >
                            {citationLabel(c)}
                          </button>
                        ) : (
                          <span key={i} className="cite">{citationLabel(c)}</span>
                        ),
                      )}
                    </div>
                  )}
                </div>
              ),
            )}
            {sending && <div className="proc-bubble assistant typing" aria-label="The assistant is answering">...</div>}
          </div>

          {disabled ? (
            <Callout tone="warn">{disabled}</Callout>
          ) : (
            <form className="proc-chat-form" onSubmit={send}>
              {remaining !== null && remaining <= 3 && (
                <p className="proc-chat-muted small" role="status">
                  {remaining === 0 ? 'No questions left this hour.' : `${remaining} ${remaining === 1 ? 'question' : 'questions'} left this hour.`}
                </p>
              )}
              <div className="row">
                <Textarea
                  aria-label="Your question"
                  placeholder="Type your question"
                  value={draft}
                  onChange={(e) => setDraft(e.currentTarget.value)}
                  onKeyDown={onKeyDown}
                  autosize
                  minRows={1}
                  maxRows={4}
                  maxLength={1000}
                  style={{ flex: 1 }}
                />
                <Button type="submit" loading={sending} disabled={!draft.trim()} aria-label="Send question" px="md">
                  <IconSend size={20} aria-hidden="true" />
                </Button>
              </div>
              {messages.length > 0 && (
                <button type="button" className="proc-textbtn" onClick={newChat}>
                  <IconRefresh size={16} aria-hidden="true" /> Start a new chat
                </button>
              )}
            </form>
          )}
        </div>
      </Drawer>
    </>
  );
}
